import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';

export type PlanningJourneyPhase = {
  rid?: number;
  rix?: string;
  shc?: string;
  shortcode?: string;
  nam?: string;
  recname?: string;
  sor?: number;
  sortorder?: number;
  ast?: number;
  status?: number;
};

type Props = {
  phases: PlanningJourneyPhase[];
  currentPhaseCode?: string | null;
  compact?: boolean;
  showCaption?: boolean;
  caption?: string;
  completedColor?: string;
  currentColor?: string;
  upcomingColor?: string;
  cancelledColor?: string;
  cancelled?: boolean;
  sx?: any;
};

class FPlanningJourney extends React.PureComponent<Props> {
  static defaultProps: Partial<Props> = {
    compact: true,
    showCaption: false,
    caption: 'Planning Journey',
    completedColor: '#9e9e9e',
    currentColor: '#1976d2',
    upcomingColor: '#e0e0e0',
    cancelledColor: '#d32f2f',
    cancelled: false,
  };

  private codeOf = (p: PlanningJourneyPhase) =>
    String(p.shc ?? p.shortcode ?? '').toUpperCase();

  private nameOf = (p: PlanningJourneyPhase) =>
    String(p.nam ?? p.recname ?? this.codeOf(p));

  private orderOf = (p: PlanningJourneyPhase) =>
    Number(p.sor ?? p.sortorder ?? 0);

  render() {
    const phases = [...(this.props.phases || [])]
      .filter((p) => Number(p.ast ?? p.status ?? 1) === 1)
      .sort((a, b) => this.orderOf(a) - this.orderOf(b));

      //console.log('phases',phases,'...',this.props.phases);

    if (!phases.length) return null;

    const currentCode = String(this.props.currentPhaseCode || '').toUpperCase();
    const currentIndex = phases.findIndex((p) => this.codeOf(p) === currentCode);

    const dotSize = this.props.compact ? 10 : 12;
    const activeSize = this.props.compact ? 13 : 16;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, ...this.props.sx }}>
        {this.props.showCaption && (
          <Typography variant="caption" color="text.secondary">
            {this.props.caption}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
          {phases.map((phase, index) => {
            const state = this.props.cancelled
              ? 'cancelled'
              : currentIndex < 0
              ? 'upcoming'
              : index < currentIndex
              ? 'completed'
              : index === currentIndex
              ? 'current'
              : 'upcoming';

            const color =
              state === 'completed'
                ? this.props.completedColor
                : state === 'current'
                ? this.props.currentColor
                : state === 'cancelled'
                ? this.props.cancelledColor
                : this.props.upcomingColor;

            const label =
              state === 'completed'
                ? 'Completed'
                : state === 'current'
                ? 'Current phase'
                : state === 'cancelled'
                ? 'Cycle cancelled'
                : 'Upcoming';

            const current = state === 'current';

            return (
              <React.Fragment key={phase.rix || phase.rid || this.codeOf(phase)}>
                {index > 0 && (
                  <Box
                    sx={{
                      width: this.props.compact ? 10 : 14,
                      height: 2,
                      bgcolor: index <= currentIndex ? this.props.completedColor : this.props.upcomingColor,
                      flexShrink: 0,
                    }}
                  />
                )}

                <Tooltip title={`${this.nameOf(phase)} — ${label}`} arrow>
                  <Box
                    component="span"
                    sx={{
                      width: current ? activeSize : dotSize,
                      height: current ? activeSize : dotSize,
                      borderRadius: '50%',
                      bgcolor: color,
                      border: current ? '2px solid rgba(25,118,210,0.20)' : '1px solid rgba(0,0,0,0.08)',
                      boxShadow: current ? '0 0 0 3px rgba(25,118,210,0.10)' : 'none',
                      transition: 'all 160ms ease',
                      cursor: 'default',
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    );
  }
}

export default FPlanningJourney;
